// ═══════════════════════════════════════════════════
//  Resizable modal — sizing arithmetic and stored payload
//
//  Pure and React-free. The client's vitest runs in a node environment with no
//  jsdom, so this is the only part of the feature that can be unit-tested;
//  keeping the localStorage calls out of here (they live in useModalSize, the
//  same split as assembly-draft-storage.ts / useAssemblyDraft.ts) is what makes
//  that possible.
//
//  Reads are defensive by design: the payload is user-writable in devtools and
//  outlives deploys, so anything unexpected is discarded in favour of the
//  default size rather than allowed to throw while the modal is opening.
// ═══════════════════════════════════════════════════

export interface ModalSize {
  /** Width in px. Always set — a wider default is the point of the feature. */
  width: number;
  /**
   * Height in px, or null for "size to content", which is what HeroUI does on
   * its own. Stays null until the user drags the corner, so an Arm with two
   * rails does not open as a mostly-empty full-height panel.
   */
  height: number | null;
}

export interface Viewport {
  width: number;
  height: number;
}

/**
 * The width at which the wider of the two panel rows stops truncating —
 * derived in the design doc from BanquetteAdditionalPanelRow, which needs
 * 1172px once its Name and Material floors are in place. Below this the drag
 * could reproduce the very bug this feature fixes.
 */
export const MIN_MODAL_WIDTH = 1180;

export const MIN_MODAL_HEIGHT = 400;

/**
 * HeroUI puts `sm:my-16` on the modal base — 4rem top plus 4rem bottom. Those
 * margins sit OUTSIDE the element's own height, so a cap expressed as a
 * fraction of the viewport overflows by up to this much and pushes the footer
 * out of reach.
 */
export const MODAL_VERTICAL_MARGIN = 128;

const MAX_WIDTH_FRACTION = 0.96;
const DEFAULT_WIDTH_FRACTION = 0.9;
const DEFAULT_WIDTH_CAP = 1600;

const STORAGE_PREFIX = 'estimator.modal-size.';

/** Storage key for one modal. Each has its own, so sizing one never moves another. */
export function modalSizeKey(storageKey: string): string {
  return `${STORAGE_PREFIX}${storageKey}`;
}

/** The widest the base may be on this viewport. */
export function maxModalWidth(viewport: Viewport): number {
  return Math.round(viewport.width * MAX_WIDTH_FRACTION);
}

/**
 * The tallest the base may be, net of HeroUI's own vertical margins. Floored at
 * MIN_MODAL_HEIGHT so a very short viewport cannot invert the clamp.
 */
export function maxModalHeight(viewport: Viewport): number {
  return Math.max(MIN_MODAL_HEIGHT, viewport.height - MODAL_VERTICAL_MARGIN);
}

/** The size to open at when nothing usable is stored. */
export function defaultModalSize(viewport: Viewport): ModalSize {
  return clampModalSize(
    {
      width: Math.min(viewport.width * DEFAULT_WIDTH_FRACTION, DEFAULT_WIDTH_CAP),
      height: null,
    },
    viewport,
  );
}

/**
 * Fit a size to the viewport.
 *
 * The floor yields to the ceiling rather than winning: on a viewport narrower
 * than MIN_MODAL_WIDTH the modal must still fit the screen, and the panel
 * list's own horizontal scroll carries the overflow.
 */
export function clampModalSize(size: ModalSize, viewport: Viewport): ModalSize {
  const maxW = maxModalWidth(viewport);
  const minW = Math.min(MIN_MODAL_WIDTH, maxW);
  const maxH = maxModalHeight(viewport);
  const minH = Math.min(MIN_MODAL_HEIGHT, maxH);
  return {
    width: Math.round(Math.min(Math.max(size.width, minW), maxW)),
    height:
      size.height === null
        ? null
        : Math.round(Math.min(Math.max(size.height, minH), maxH)),
  };
}

export function serialiseModalSize(size: ModalSize): string {
  return JSON.stringify(size);
}

/** The stored size, or null when nothing usable is stored. Never throws. */
export function parseModalSize(raw: string | null): ModalSize | null {
  if (!raw) return null;
  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch {
    return null;
  }
  if (typeof parsed !== 'object' || parsed === null || Array.isArray(parsed)) return null;

  const { width, height } = parsed as Record<string, unknown>;
  if (typeof width !== 'number' || !Number.isFinite(width)) return null;

  // An absent height is the content-sized state, not a corrupt payload — it is
  // what every size written before the first vertical drag looks like.
  if (height === undefined || height === null) return { width, height: null };
  if (typeof height !== 'number' || !Number.isFinite(height)) return null;
  return { width, height };
}
